// WHAT: Table component for displaying time entries with sorting and filtering
// WHY:  Provides detailed view of time tracking entries with interactive controls
// DOMAIN: Productivity → TimeTracQ
// DEPENDENCIES: useFilter hook, core.invoke
// RISKS:
//   - NOTE: Sorting may not work correctly with null/undefined values
//   - NOTE: Table may have performance issues with many entries
//   - NOTE: Date/time formatting may not match user locale

import { FunnelIcon } from "@heroicons/react/24/outline";
import { core } from "@tauri-apps/api";
import { useState, useEffect, useMemo } from "react";

import { Card, Button } from "@/components/ui";

// Reason: Dependency cycle occurs because useFilter uses the domain barrel export which exports 'components',
// creating a cycle: TimeEntriesTable → useFilter → @/domains/productivity/timetracq → components → organisms → TimeEntriesTable.
// Importing directly from the source file breaks the cycle.
import { useFilter } from "../molecules/FilterContextProvider.shared";

/**
 * Time entry interface
 */
interface TimeEntry {
  /** Aspect name */
  aspect: string;
  /** Duration in seconds */
  duration_seconds: number;
  /** End time (ISO) */
  end_time: string;
  /** Entry ID */
  id: string;
  /** Optional notes */
  notes?: string;
  /** Start time (ISO) */
  start_time: string;
  /** Workspace name */
  workspace: string;
}

/**
 * Backend response interface
 */
interface BackendResponse {
  /** Period sessions */
  period_sessions?: TimeEntry[];
  /** Total minutes */
  total_minutes?: number;
}

/**
 * Sort key type
 */
type SortKey = "aspect" | "date" | "duration" | "notes" | "time" | "workspace";

/**
 * WHAT:
 *   Table component that displays time tracking entries with sorting,
 *   filtering, and detailed information.
 *
 * WHY:
 *   Enables users to review and analyze their time tracking data in a
 *   structured table format.
 *
 * BEHAVIOR:
 *   - Fetches time entries from backend
 *   - Displays entries in sortable table
 *   - Supports sorting by multiple columns
 *   - Shows total minutes for period
 *   - Updates when filter changes
 *
 * RISKS:
 *   - NOTE: Sorting may not work correctly with null/undefined values
 *   - NOTE: Table may have performance issues with many entries
 *   - NOTE: Date/time formatting may not match user locale
 *
 * @returns Time entries table component
 *
 */
// Formatting functions moved to outer scope
/**
 * Formats duration in seconds to human-readable string
 *
 * @param seconds - Duration in seconds
 *
 * @returns Formatted duration string (e.g., "1h 30m 45s")
 *
 */
const formatDuration = (seconds: number): string => {
  const hours = Math.floor(seconds / 3600);
  const minutes = Math.floor((seconds % 3600) / 60);
  const secs = seconds % 60;
  return `${String(hours)}h ${String(minutes).padStart(2, "0")}m ${String(secs).padStart(2, "0")}s`;
};

/**
 * Formats date string to time string
 *
 * @param dateString - ISO date string
 *
 * @returns Formatted time string (e.g., "14:30")
 *
 */
const formatTime = (dateString: string): string => {
  const date = new Date(dateString);
  return date.toLocaleTimeString([], { hour: "2-digit", minute: "2-digit" });
};

/**
 * Formats date string to date string
 *
 * @param dateString - ISO date string
 *
 * @returns Formatted date string (e.g., "01.01.2024")
 *
 */
const formatDate = (dateString: string): string => {
  const date = new Date(dateString);
  return date.toLocaleDateString("de-DE");
};

/**
 * Formats total time in minutes to human-readable string
 *
 * @param minutes - Total time in minutes
 *
 * @returns Formatted time string (e.g., "1h 30m 45s")
 *
 */
const formatTotalTime = (minutes: number): string => {
  const hours = Math.floor(minutes / 60);
  const mins = Math.floor(minutes % 60);
  const secs = Math.floor((minutes % 1) * 60);
  return `${String(hours)}h ${String(mins).padStart(2, "0")}m ${String(secs).padStart(2, "0")}s`;
};

export const TimeEntriesTable = () => {
  const { dateFilter, period } = useFilter();

  const [entries, setEntries] = useState<TimeEntry[]>([]);
  const [totalMinutes, setTotalMinutes] = useState<number>(0);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<null | string>(null);

  const [sortKey, setSortKey] = useState<SortKey>("date");
  const [asc, setAsc] = useState(false);

  // Backend laden
  useEffect(() => {
    setLoading(true);
    setError(null);
    void (async () => {
      try {
        const res = await core.invoke<BackendResponse>("get_aggregated_summary", {
          filter: dateFilter,
        });
        setEntries(res.period_sessions ?? []);
        setTotalMinutes(res.total_minutes ?? 0);
      } catch {
        setError("Fehler beim Laden der Daten");
      } finally {
        setLoading(false);
      }
    })();
  }, [dateFilter]);

  // Sortierung
  const sortedEntries = useMemo(() => {
    // Reason: toSorted() is not recognized by TypeScript, but is available in runtime
    const sorted = (
      [...entries] as TimeEntry[] & {
        toSorted: (compareFn: (a: TimeEntry, b: TimeEntry) => number) => TimeEntry[];
        // Reason: toSorted() is not recognized by TypeScript, but is available in runtime
      }
    ).toSorted((a: TimeEntry, b: TimeEntry) => {
      let aValue: Date | number | string | undefined, bValue: Date | number | string | undefined;

      switch (sortKey) {
        case "workspace": {
          aValue = a.workspace;
          bValue = b.workspace;
          break;
        }
        case "aspect": {
          aValue = a.aspect;
          bValue = b.aspect;
          break;
        }
        case "notes": {
          aValue = a.notes ?? "";
          bValue = b.notes ?? "";
          break;
        }
        case "date":
        case "time": {
          aValue = new Date(a.start_time);
          bValue = new Date(b.start_time);
          break;
        }
        case "duration": {
          aValue = a.duration_seconds;
          bValue = b.duration_seconds;
          break;
        }
        default: {
          return 0;
        }
      }

      if (aValue < bValue) {
        return asc ? -1 : 1;
      }
      if (aValue > bValue) {
        return asc ? 1 : -1;
      }
      return 0;
    });
    return sorted;
  }, [entries, sortKey, asc]);

  const toggleSort = (key: SortKey) => {
    setAsc(key === sortKey ? !asc : true);
    setSortKey(key);
  };

  return (
    <div className="space-y-6">
      {/* Header mit Total Time und Controls */}
      <Card
        className="bg-slate-700/30 border-slate-600/50 shadow-tracq-card"
        padding="lg"
        variant="elevated"
      >
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-6">
            <div>
              <h3 className="text-2xl font-bold text-white">Time Entries</h3>
              <p className="text-slate-400 text-sm">Detaillierte Zeitaufzeichnungen ({period})</p>
            </div>
            <div className="text-right">
              <div className="text-sm text-slate-400">Total Time</div>
              <div className="text-3xl font-bold text-tracq-bright-blue">
                {formatTotalTime(totalMinutes)}
              </div>
            </div>
          </div>

          <div className="flex items-center gap-3">
            <Button size="sm" variant="secondary">
              <FunnelIcon className="w-4 h-4 mr-2" />
              Filter
            </Button>
            <Button size="sm" variant="primary">
              + Add time entry
            </Button>
          </div>
        </div>
      </Card>

      {/* Tabelle */}
      <Card
        className="bg-slate-700/30 border-slate-600/50 shadow-tracq-card"
        padding="lg"
        variant="elevated"
      >
        {(() => {
          if (loading) {
            return (
              <div className="h-40 flex items-center justify-center text-slate-500 animate-pulse">
                Loading…
              </div>
            );
          }
          if (error) {
            return (
              <div className="h-40 flex items-center justify-center text-red-400">{error}</div>
            );
          }
          if (entries.length === 0) {
            return (
              <div className="h-40 flex items-center justify-center text-slate-300">
                Keine Zeitaufzeichnungen für den gewählten Zeitraum
              </div>
            );
          }
          return (
            <div className="overflow-x-auto">
              <table className="min-w-full text-sm">
                <thead>
                  <tr className="border-b border-slate-600">
                    <Th
                      active={sortKey === "workspace"}
                      asc={asc}
                      label="Workspace"
                      onClick={() => {
                        toggleSort("workspace");
                      }}
                    />
                    <Th
                      active={sortKey === "aspect"}
                      asc={asc}
                      label="Aspect"
                      onClick={() => {
                        toggleSort("aspect");
                      }}
                    />
                    <Th
                      active={sortKey === "notes"}
                      asc={asc}
                      label="Notes"
                      onClick={() => {
                        toggleSort("notes");
                      }}
                    />
                    <Th
                      active={sortKey === "date"}
                      asc={asc}
                      label="Date"
                      onClick={() => {
                        toggleSort("date");
                      }}
                    />
                    <Th
                      active={sortKey === "time"}
                      asc={asc}
                      label="Time"
                      onClick={() => {
                        toggleSort("time");
                      }}
                    />
                    <Th
                      active={sortKey === "duration"}
                      asc={asc}
                      label="Duration"
                      onClick={() => {
                        toggleSort("duration");
                      }}
                    />
                  </tr>
                </thead>
                <tbody>
                  {sortedEntries.map((entry: TimeEntry) => (
                    <tr
                      className="border-b border-slate-700 hover:bg-slate-700/50 transition-colors"
                      key={entry.id}
                    >
                      <td className="px-4 py-3 text-white font-medium">{entry.workspace}</td>
                      <td className="px-4 py-3 text-slate-300">{entry.aspect}</td>
                      <td className="px-4 py-3 text-slate-400 max-w-xs">
                        {entry.notes ? (
                          <div className="truncate" title={entry.notes}>
                            {entry.notes}
                          </div>
                        ) : (
                          <span className="text-slate-500">—</span>
                        )}
                      </td>
                      <td className="px-4 py-3 text-slate-300">{formatDate(entry.start_time)}</td>
                      <td className="px-4 py-3 text-slate-300">
                        {formatTime(entry.start_time)} - {formatTime(entry.end_time)}
                      </td>
                      <td className="px-4 py-3 text-white font-semibold">
                        {formatDuration(entry.duration_seconds)}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          );
        })()}
      </Card>
    </div>
  );
};

/**
 * Table Header component with sort icon
 *
 * @param props - Component props
 *
 * @param props.label - Column label
 *
 * @param props.onClick - Click handler for sorting
 *
 * @param props.active - Whether this column is currently sorted
 *
 * @param props.asc - Whether sorting is ascending
 *
 * @returns Table header cell with sort indicator
 *
 */
function Th({
  active,
  asc,
  label,
  onClick,
}: Readonly<{
  label: string;
  onClick: () => void;
  active: boolean;
  asc: boolean;
}>) {
  return (
    <th
      className={`px-4 py-3 cursor-pointer select-none hover:text-white transition-colors text-left ${
        active ? "text-white" : "text-slate-400"
      }`}
      onClick={onClick}
    >
      <span className="inline-flex items-center gap-1">
        <span>{label}</span>
        {active ? <span className="text-xs text-slate-500">{asc ? "↑" : "↓"}</span> : null}
      </span>
    </th>
  );
}

// Named export only - no default export
